import { useState, useEffect } from 'react';
import ShareCard from './ShareCard';
import { HiAcademicCap, HiLightningBolt, HiLightBulb, HiClipboardCopy, HiCheck } from 'react-icons/hi';
import { FaRocket, FaSearch, FaBullseye, FaGem, FaCrown, FaHatWizard, FaTrophy } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { trackButtonClick, trackShare, trackResultsView, trackRetakeAssessment, trackExternalLink } from '../services/analytics';
import { useAuth } from '../firebase/AuthContext';
import Navbar from '../components/Navbar';
import { getLevelIcon } from '../components/LevelIcons';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ReferenceLine } from 'recharts';
import FeedbackForm from '../components/FeedbackForm';
import GoogleSignInButton from '../components/GoogleSignInButton';
import tagToDescription from '../resources/tag_to_description.json';

const Results = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [resultsData, setResultsData] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [stats, setStats] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [copiedPrompt, setCopiedPrompt] = useState(null);

  useEffect(() => {
    const storedData = sessionStorage.getItem('quizResults');
    if (!storedData) {
      navigate('/');
      return;
    }
    
    const data = JSON.parse(storedData);
    setResultsData(data);
    loadResults(data);
  }, [navigate]);

  const loadResults = async (data) => {
    try {
      console.log('📊 Results data loaded:', data);
      console.log('📊 Recommendations:', data.recommendations);
      
      // Track results view
      const levelInfo = getLevelInfo(data.final_level);
      trackResultsView(data.final_level, levelInfo.name, calculatePercentile(data.final_level));
      
      // Get stats for comparison
      const statsResponse = await apiService.getStats();
      if (statsResponse.success) {
        setStats(statsResponse.stats);
      }

      // Recommendations are already included in data from getFinalResults
      if (data.recommendations) {
        console.log('✅ Setting recommendations:', data.recommendations);
        setRecommendations(data.recommendations);
      } else {
        console.log('⚠️ No recommendations found in data');
      }
    } catch (error) {
      console.error('Failed to load results:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLevelInfo = (level) => {
    const levels = {
      0: { name: 'AI Noob', icon: HiAcademicCap },
      1: { name: 'AI Novice', icon: FaRocket },
      2: { name: 'AI Explorer', icon: FaSearch },
      3: { name: 'AI Practitioner', icon: HiLightningBolt },
      4: { name: 'AI Specialist', icon: FaBullseye },
      5: { name: 'AI Expert', icon: FaGem },
      6: { name: 'AI Master', icon: FaCrown },
      7: { name: 'AI Guru', icon: FaHatWizard }
    };
    return levels[level] || levels[0];
  };


  const calculatePercentile = (level) => {
    if (!stats || !stats.level_distribution) return 50;
    
    // Calculate total users from level_distribution
    const totalUsers = Object.values(stats.level_distribution).reduce((sum, count) => sum + count, 0) || 1;
    let usersBelow = 0;
    for (let l = 0; l < level; l++) {
      usersBelow += stats.level_distribution[l] || 0;
    }
    
    const percentile = Math.round((usersBelow / totalUsers) * 100);
    return 100 - percentile;
  };

  const calculateAverageLevel = () => {
    if (!stats || !stats.level_distribution) return 0;
    
    let totalScore = 0;
    let totalUsers = 0;
    
    Object.entries(stats.level_distribution).forEach(([level, count]) => {
      totalScore += parseInt(level) * count;
      totalUsers += count;
    });
    
    return totalUsers > 0 ? (totalScore / totalUsers).toFixed(1) : 0;
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

const handleShare = (platform) => {
  if (!resultsData) return;

  const level = getLevelInfo(resultsData.final_level);
  const shareUrl = 'https://aiexam.educado.tech';

  // Track share event
  trackShare(platform, 'results_page');

  // Robustly compute totals from resultsData at click-time
  const localTotalQuestions = Number.isFinite(resultsData.total_questions)
    ? resultsData.total_questions
    : (parseInt(resultsData.total_questions) || resultsData.questions?.length || 0);
  const localWrongCount = Array.isArray(resultsData.wrong_questions) ? resultsData.wrong_questions.length : 0;
  const localTotalCorrect = Math.max(0, localTotalQuestions - localWrongCount);
  const localPercentage = localTotalQuestions > 0 ? ((localTotalCorrect / localTotalQuestions) * 100).toFixed(1) : 0;

  const shareText = `Hey! I just took this 10-minute AI Knowledge Test and it tells you your current level.\nIt's free and anonymous, and it shows where you stand and what to learn next. Want to try it?`;
  
  const twitterText = `${shareText}\n\n#AI #ArtificialIntelligence #MachineLearning #AISkills #LearnAI`;

  if (platform === 'twitter') {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterText)}&url=${encodeURIComponent(shareUrl)}`,
      '_blank'
    );
    return;
  }

  if (platform === 'whatsapp') {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(shareText + '\n' + shareUrl)}`,
      '_blank'
    );
    return;
  }

  if (platform === 'copy') {
    navigator.clipboard.writeText(shareUrl);
    alert('Link copied to clipboard!');
    return;
  }
};


  if (loading || !resultsData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-14 h-14 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600 text-base">Analyzing your results...</p>
        </div>
      </div>
    );
  }

  // Compute score values from resultsData so they're available throughout the component
  const totalQuestions = (() => {
    if (Number.isFinite(resultsData.total_questions)) return resultsData.total_questions;
    if (Array.isArray(resultsData.questions)) return resultsData.questions.length;
    if (Array.isArray(resultsData.all_questions)) return resultsData.all_questions.length;
    if (Array.isArray(resultsData.answers)) return resultsData.answers.length;
    if (Array.isArray(resultsData.wrong_questions) && Number.isFinite(resultsData.total_correct)) {
      return resultsData.wrong_questions.length + Number(resultsData.total_correct);
    }
    if (Number.isFinite(resultsData.total_correct)) return Number(resultsData.total_correct);
    if (Array.isArray(resultsData.wrong_questions)) return resultsData.wrong_questions.length;
    return 0;
  })();

  const wrongCount = Array.isArray(resultsData.wrong_questions) ? resultsData.wrong_questions.length : 0;
  const totalCorrect = Number.isFinite(resultsData.total_correct) ? Number(resultsData.total_correct) : Math.max(0, totalQuestions - wrongCount);
  const percentage = totalQuestions > 0 ? ((totalCorrect / totalQuestions) * 100).toFixed(1) : 0;

  // Download the share card image from the hidden canvas
  const downloadImage = () => {
    const canvas = document.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'educado-result.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const levelInfo = getLevelInfo(resultsData.final_level);
  // Always use calculated level name based on final_level number for accuracy
  const displayLevelName = levelInfo.name;
  // Get icon from centralized LevelIcons module
  const LevelIcon = getLevelIcon(resultsData.final_level, displayLevelName);
  // Use percentile from API response instead of calculating
  const percentile = resultsData.percentile || calculatePercentile(resultsData.final_level);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <Navbar />

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        
        {/* Thank You Message */}
        <div className="text-center mb-10 md:mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Thank You for Taking the <span className="text-primary">Educado AI</span> Test!
          </h2>
          <p className="text-gray-600 text-base md:text-lg">
            We hope you learned something new about AI and your knowledge level.
          </p>
        </div>

        {/* Achievement Section */}
        <section className="bg-white rounded-xl border border-gray-200 p-8 md:p-10 mb-8 md:mb-10 text-center shadow-sm">
          <p className="text-gray-500 text-sm mb-3">
            Your level in AI is
          </p>
          <div className="text-6xl md:text-7xl mb-5 flex justify-center items-center">
            <LevelIcon className="text-primary" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">{displayLevelName}</h1>
          <p className="text-lg md:text-xl text-gray-600 mb-5">
            Level <span className="font-bold text-primary">{resultsData.final_level}</span> of 7
          </p>
          <div className="max-w-md mx-auto">
            <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
              <div
                className="bg-primary h-full rounded-full transition-all duration-500"
                style={{ width: `${(resultsData.final_level / 7) * 100}%` }}
              ></div>
            </div>
          </div>
        </section>

                {/* Review Answers Section */}
        {resultsData.wrong_questions && resultsData.wrong_questions.length > 0 && (
          <>
            {!showReview && (
              <section className="mb-8 text-center">
                <div className="flex justify-center mb-4">
                  <button
                    onClick={() => setShowReview(true)}
                    className="px-6 md:px-8 py-3 text-base md:text-lg font-semibold text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors shadow-md"
                  >
                    Review Wrong Answers
                  </button>
                </div>

              </section>
            )}
            
            {showReview && (
              <section className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <span>📝</span>
                    Review Your Wrong Answers
                  </h2>
                  <button 
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    onClick={() => setShowReview(false)}
                  >
                    Close Review
                  </button>
                </div>
                
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-gray-700 text-sm md:text-base">
                    You got <strong className="text-primary">{resultsData.wrong_questions.length} question{resultsData.wrong_questions.length > 1 ? 's' : ''}</strong> wrong. 
                    Review them below to learn from your mistakes.
                  </p>
                </div>
                
                <div className="space-y-5">
                  {resultsData.wrong_questions.map((q, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg border border-gray-200 p-5">
                      <div className="flex justify-between items-center mb-4">
                        <span className="px-3 py-1 bg-primary text-white text-sm font-semibold rounded-full">
                          Question {index + 1}
                        </span>
                        <span className="px-3 py-1 bg-gray-200 text-gray-700 text-sm font-medium rounded-full">
                          {q.level_name || `Level ${q.level}`}
                        </span>
                      </div>
                      <div className="text-base font-medium text-gray-900 mb-4">{q.question}</div>
                      <div className="space-y-3 mb-4">
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                          <span className="block text-sm font-semibold text-red-600 mb-1">
                            Your Answer ({q.selected}):
                          </span>
                          <span className="text-sm text-gray-700">{q.selected_text || `Option ${q.selected}`}</span>
                        </div>
                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                          <span className="block text-sm font-semibold text-green-600 mb-1">
                            Correct Answer ({q.correct}):
                          </span>
                          <span className="text-sm text-gray-700">{q.correct_text || `Option ${q.correct}`}</span>
                        </div>
                      </div>
                      {q.explanation && (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-3">
                          <span className="block text-sm font-semibold text-blue-600 mb-1">💡 Explanation:</span>
                          <p className="text-sm text-gray-700">{q.explanation}</p>
                        </div>
                      )}
                      {q.topic && (
                        <div>
                          <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full">
                            {typeof q.topic === 'string' ? q.topic : (q.topic.tag || q.topic.name || q.topic.description || JSON.stringify(q.topic))}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {/* Anonymous Results Info (only if not logged in) */}
        {!user && (
          <section className="bg-blue-50 border-2 border-blue-200 rounded-lg p-8 md:p-10 mb-8 text-center">
            <div className="text-4xl mb-4">ℹ️</div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
              Results Saved in Community Stats
            </h3>
            <p className="text-base md:text-lg text-gray-700 mb-6 max-w-2xl mx-auto">
              Your results have been added to our community statistics, but are not saved to a personal account. 
              To track your progress over time, view your test history, and save results permanently, please 
              <span className="font-semibold text-primary"> log in or sign up before taking the assessment</span>.
            </p>
            
            <div className="flex justify-center">
              <GoogleSignInButton variant="primary" />
            </div>
          </section>
        )}


        {/* Topics To Study (from API) */}
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 md:p-10 mb-8 md:mb-10">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center gap-2">
            <HiLightBulb className="text-primary text-2xl" />
            Topics To Study
          </h2>
          <p className="text-gray-600 mb-6">Copy these prompts and paste them into your favorite AI tool (ChatGPT, Claude, Gemini, etc.) to learn about each topic.</p>

          {resultsData.topics_to_study && resultsData.topics_to_study.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {resultsData.topics_to_study.map((t, idx) => {
                const tag = t.tag || t.name || '';
                const prompt = tagToDescription[tag] || t.description || t.desc || `Explain ${tag.replace(/-/g, ' ')} in AI context.`;
                const isCopied = copiedPrompt === idx;
                
                return (
                  <div key={idx} className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex flex-col h-full">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary/10 text-primary text-lg flex-shrink-0">
                          <HiLightBulb />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 capitalize">{tag.replace(/-/g, ' ')}</h3>
                      </div>
                      <p className="text-sm text-gray-600 mb-4 flex-1">{prompt}</p>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(prompt);
                          setCopiedPrompt(idx);
                          trackButtonClick('copy_learning_prompt', { topic: tag });
                          setTimeout(() => setCopiedPrompt(null), 2000);
                        }}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          isCopied 
                            ? 'bg-green-100 text-green-700 border border-green-300' 
                            : 'bg-primary/10 text-primary hover:bg-primary/20 border border-primary/30'
                        }`}
                      >
                        {isCopied ? (
                          <><HiCheck className="text-lg" /> Copied!</>
                        ) : (
                          <><HiClipboardCopy className="text-lg" /> Copy Prompt</>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-6 bg-gray-50 rounded-lg border border-gray-200 text-center">
              <p className="text-gray-600">No specific topics suggested — great job! Keep practicing to improve further.</p>
            </div>
          )}
        </section>

        {/* Community Comparison */}
        {stats && (
          <section className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-8">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">How You Compare</h2>
            
            {/* Stats Summary */}
            <div className="grid gap-5 md:grid-cols-3 mb-8">
              <div className="text-center p-5 md:p-6 bg-primary/5 rounded-lg border border-primary/20">
                <div className="text-3xl md:text-4xl font-bold text-primary mb-2">{100 - percentile}{100 - percentile === 1 ? 'st' : 100 - percentile === 2 ? 'nd' : 100 - percentile === 3 ? 'rd' : 'th'} Percentile</div>
                <div className="text-gray-600 font-medium text-sm">Your position among all test attempts</div>
              </div>
              <div className="text-center p-5 md:p-6 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                  {formatNumber(Object.values(stats.level_distribution || {}).reduce((sum, count) => sum + count, 0))}
                </div>
                <div className="text-gray-600 font-medium text-sm">Total Tests Attempted</div>
              </div>
              <div className="text-center p-5 md:p-6 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{calculateAverageLevel()}</div>
                <div className="text-gray-600 font-medium text-sm">Average Level</div>
              </div>
            </div>

            {/* Level Distribution Chart */}
            {stats.level_distribution && Object.keys(stats.level_distribution).length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 md:p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Level Distribution</h3>
                <p className="text-sm text-gray-600 mb-4 text-center">Your position is highlighted in purple</p>
                <div className="max-w-3xl mx-auto">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={[
                        { level: 0, name: 'AI Noob', color: '#F3E8FF' },
                        { level: 1, name: 'AI Novice', color: '#E9D5FF' },
                        { level: 2, name: 'AI Explorer', color: '#D6BCFA' },
                        { level: 3, name: 'AI Practitioner', color: '#B794F4' },
                        { level: 4, name: 'AI Specialist', color: '#9F7AEA' },
                        { level: 5, name: 'AI Expert', color: '#7C3AED' },
                        { level: 6, name: 'AI Master', color: '#6D28D9' },
                        { level: 7, name: 'AI Guru', color: '#4C1D95' }
                      ].map((item) => ({
                        name: `${item.level}`,
                        fullName: item.name,
                        value: stats.level_distribution[item.level] || 0,
                        color: item.color,
                        isUserLevel: item.level === resultsData.final_level
                      }))}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fontSize: 12 }}
                        label={{ value: 'Level', position: 'bottom', offset: 40 }}
                      />
                      <YAxis 
                        label={{ value: 'Attempts', angle: -90, position: 'insideLeft', offset: 10 }}
                      />
                      <Tooltip 
                        formatter={(value, name, props) => [`${value} attempts`, props.payload.fullName]}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {[
                          { level: 0, color: '#F3E8FF' },
                          { level: 1, color: '#E9D5FF' },
                          { level: 2, color: '#D6BCFA' },
                          { level: 3, color: '#B794F4' },
                          { level: 4, color: '#9F7AEA' },
                          { level: 5, color: '#7C3AED' },
                          { level: 6, color: '#6D28D9' },
                          { level: 7, color: '#4C1D95' }
                        ].map((item, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={item.level === resultsData.final_level ? '#7C3AED' : item.color}
                            stroke={item.level === resultsData.final_level ? '#4C1D95' : 'none'}
                            strokeWidth={item.level === resultsData.final_level ? 3 : 0}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-sm text-gray-500 mt-2 text-center">
                  You reached <span className="font-semibold text-primary">Level {resultsData.final_level} ({displayLevelName})</span>
                </p>
              </div>
            )}
          </section>
        )}

        {/* CTA Section */}
        <section className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center mb-8">
          <a 
            href="/quiz" 
            className="inline-flex items-center justify-center gap-2 px-6 md:px-8 py-3 text-base font-semibold text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors shadow-md hover:shadow-lg"
          >
            <span>Try Again</span>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 4v6h6M23 20v-6h-6"/>
              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
            </svg>
          </a>
          <a 
            href="/" 
            className="inline-flex items-center justify-center px-6 md:px-8 py-3 text-base font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Back to Home
          </a>
        </section>

        {/* Share Section */}
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 md:p-10 mb-8 md:mb-10 text-center">
          {/* Hidden ShareCard Canvas for Download */}
          <ShareCard
            levelName={displayLevelName}
            score={`${totalCorrect}/${totalQuestions}`}
            percentage={percentage}
          />
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
            📢 Share <span className="text-primary">Educado AI</span> with Friends!
          </h3>
          <p className="text-gray-600 text-base mb-6">
            You've started your AI journey. Share this test with a friend to help them start theirs too.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button 
              className="px-5 py-2.5 font-medium text-white bg-black rounded-lg hover:bg-gray-800 transition-colors text-sm"
              onClick={() => handleShare('twitter')}
            >
              <span>Share on X</span>
            </button>
            <button 
              className="px-5 py-2.5 font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors text-sm"
              onClick={() => handleShare('whatsapp')}
            >
              <span>Share on WhatsApp</span>
            </button>
            <button 
              className="px-5 py-2.5 font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              onClick={() => handleShare('copy')}
            >
              <span>Copy Link</span>
            </button>
            {/* <button
              className="px-5 py-2.5 font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors text-sm"
              onClick={downloadImage}
            >
              <span>Download Share Image</span>
            </button> */}
            {/* <button
              className="px-5 py-2.5 font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors text-sm"
              onClick={downloadImage}
            >
              <span>Download Share Image</span>
            </button> */}
          </div>
        </section>

        {/* Feedback Section */}
        <section className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-8 text-center">
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Send Us Feedback</h3>
          <p className="text-gray-600 mb-4">Share your experience — feedback helps us improve the assessment and recommendations.</p>
          <div className="flex justify-center">
            <button
              onClick={() => {
                trackButtonClick('give_feedback', 'results_page');
                setShowFeedbackModal(true);
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white text-base font-semibold rounded-lg hover:bg-primary-dark transition-colors shadow-md"
            >
              Give Feedback
            </button>
          </div>
          {showFeedbackModal && (
            <FeedbackForm modal onClose={() => setShowFeedbackModal(false)} autoFocus />
          )}
        </section>

        {/* Follow Us Section */}
        <section className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-8 text-center">
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
            👋 Follow <span className="text-primary">Educado AI</span> on Social Media!
          </h3>
          <p className="text-gray-600 text-base mb-6">
            Stay updated with AI learning tips, new features, and more!
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a 
              href="https://www.linkedin.com/company/educado-ai/?utm_source=results_page&utm_medium=social&utm_campaign=follow" 
              target="_blank" 
              rel="noopener noreferrer" 
              onClick={() => trackExternalLink('https://www.linkedin.com/company/educado-ai/', 'linkedin_results')}
              className="inline-flex items-center gap-2 px-5 py-2.5 font-medium text-white bg-[#0A66C2] rounded-lg hover:bg-[#004182] transition-all text-sm"
            >
              <span className="text-lg">💼</span>
              <span>LinkedIn</span>
            </a>
            <a 
              href="https://www.instagram.com/educadoai?utm_source=results_page&utm_medium=social&utm_campaign=follow" 
              target="_blank" 
              rel="noopener noreferrer" 
              onClick={() => trackExternalLink('https://www.instagram.com/educadoai', 'instagram_results')}
              className="inline-flex items-center gap-2 px-5 py-2.5 font-medium text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg hover:shadow-md transition-all text-sm"
            >
              <span className="text-lg">📸</span>
              <span>Instagram</span>
            </a>
            {/* X, YouTube - commented out until pages are ready
            <a 
              href="https://x.com/EducadoAI" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="inline-flex items-center gap-2 px-5 py-2.5 font-medium text-white bg-black rounded-lg hover:bg-gray-800 transition-all text-sm"
            >
              <span className="text-lg">𝕏</span>
              <span>X (Twitter)</span>
            </a>
            <a 
              href="https://youtube.com/@educadoai" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="inline-flex items-center gap-2 px-5 py-2.5 font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-all text-sm"
            >
              <span className="text-lg">▶️</span>
              <span>YouTube</span>
            </a>
            */}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Results;
